/**
 * 🟥 CUADRO NEGRO 2026-02-20 — "Mi Suscripción".
 *
 * Panel dentro de Configuración (subsección activada desde SettingsModal)
 * que muestra el estado de la suscripción Premium del usuario y expone
 * todos los botones que el Cuadro Negro exige:
 *
 *   - Actualizar a Premium      (solo Free)
 *   - Cambiar tarjeta           → flow=payment_method_update
 *   - Ver facturas              → flow=invoice_history
 *   - Cancelar suscripción      → flow=subscription_cancel
 *   - Administrar suscripción completa → portal genérico (sin flow)
 *
 * Todos los botones llaman al mismo endpoint POST /api/billing/portal y
 * redirigen al usuario a la URL devuelta por Stripe.
 *
 * Estados manejados:
 *   - loading inicial (carga /api/billing/summary)
 *   - error de red (mensaje amable, sin detalles técnicos)
 *   - portal_unavailable (mensaje amable, ver bullet)
 */
import { useEffect, useState } from 'react';
import {
  ArrowLeft, CreditCard, Receipt, XCircle, Crown,
  Loader2, Settings as SettingsIcon, Sparkles,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { openSubscriptionPanel } from '../utils/openSubscriptionPanel';
import { PREMIUM_BENEFITS } from '../constants/premiumBenefits';

const t = {
  es: {
    title: 'Mi Suscripción',
    subtitle: 'Gestioná tu plan Premium y tu información de facturación.',
    back: 'Volver',
    loading: 'Cargando datos de tu suscripción…',
    cardTitle: 'Tu plan actual',
    plan: 'Plan actual',
    planFree: 'Free',
    planPremium: 'Premium',
    status: 'Estado',
    statusActive: 'Activo',
    statusCancelled: 'Cancelado',
    statusInactive: 'Inactivo',
    statusExpiresOn: 'Expira el',
    renewal: 'Fecha de renovación',
    paymentMethod: 'Método de pago',
    paymentMethodNone: 'Sin método de pago registrado',
    price: 'Precio',
    pricePremium: 'USD 9.99 / mes',
    priceFree: 'Gratis',
    btnUpgrade: 'Actualizar a Premium',
    btnChangeCard: 'Cambiar tarjeta',
    btnInvoices: 'Ver facturas',
    btnCancel: 'Cancelar suscripción',
    btnFullPortal: 'Administrar suscripción completa',
    portalUnavailable: 'El portal de facturación todavía no está disponible para tu cuenta. Si activaste Premium hace pocos minutos, esperá un instante y volvé a intentar.',
    networkError: 'No pudimos conectar con el servicio de facturación. Intentá de nuevo en unos segundos.',
    redirecting: 'Abriendo Stripe…',
  },
  en: {
    title: 'My Subscription',
    subtitle: 'Manage your Premium plan and billing info.',
    back: 'Back',
    loading: 'Loading your subscription…',
    cardTitle: 'Your current plan',
    plan: 'Current plan',
    planFree: 'Free',
    planPremium: 'Premium',
    status: 'Status',
    statusActive: 'Active',
    statusCancelled: 'Cancelled',
    statusInactive: 'Inactive',
    statusExpiresOn: 'Expires on',
    renewal: 'Renewal date',
    paymentMethod: 'Payment method',
    paymentMethodNone: 'No payment method on file',
    price: 'Price',
    pricePremium: 'USD 9.99 / month',
    priceFree: 'Free',
    btnUpgrade: 'Upgrade to Premium',
    btnChangeCard: 'Change card',
    btnInvoices: 'View invoices',
    btnCancel: 'Cancel subscription',
    btnFullPortal: 'Manage full subscription',
    portalUnavailable: 'The billing portal is not available for your account yet. If you just activated Premium, wait a moment and try again.',
    networkError: 'We could not reach the billing service. Please retry in a few seconds.',
    redirecting: 'Opening Stripe…',
  },
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-zinc-800/60 last:border-b-0">
    <span className="text-xs text-zinc-400 uppercase tracking-wide">{label}</span>
    <span className="text-sm font-medium text-zinc-100" data-testid={testId}>{value}</span>
  </div>
);

const MySubscriptionPanel = ({ language = 'es', onBack }) => {
  const L = t[language] || t.es;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // qué botón está en loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/billing/summary`, { withCredentials: true });
        if (!cancelled) setSummary(res.data);
      } catch (e) {
        if (!cancelled) {
          toast.error(L.networkError);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [L.networkError]);

  const isPremium = Boolean(summary?.is_premium);

  const handlePortal = async (flow, btnId) => {
    setActionLoading(btnId);
    try {
      const res = await axios.post(
        `${API}/billing/portal`,
        flow ? { flow } : {},
        { withCredentials: true },
      );
      const url = res?.data?.url;
      const err = res?.data?.error;
      if (url) {
        toast.success(L.redirecting, { duration: 1500 });
        // Redirigimos a Stripe en la misma pestaña → vuelve al return_url.
        window.location.href = url;
        return;
      }
      // Soft-fail desde el backend (200 con error code).
      if (err === 'portal_unavailable' || err === 'customer_unavailable') {
        toast.error(L.portalUnavailable, { duration: 5000 });
      } else {
        toast.error(L.networkError);
      }
    } catch (e) {
      toast.error(L.networkError);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpgrade = () => {
    // 🟥 CUADRO NEGRO Panel de Suscripción Único (feb-2026): Free users
    // van al Panel de Suscripción real ($9/$86), NO al Stripe Billing
    // Portal y NO a una página informativa.
    openSubscriptionPanel();
  };

  const renderStatus = () => {
    if (!isPremium) return L.statusInactive;
    const s = (summary?.status || '').toLowerCase();
    if (summary?.cancel_at_period_end && summary?.renewal_date) {
      return `${L.statusExpiresOn} ${summary.renewal_date}`;
    }
    if (s === 'active' || s === 'trialing') return L.statusActive;
    if (s === 'canceled' || s === 'cancelled') return L.statusCancelled;
    return L.statusActive;
  };

  const renderPaymentMethod = () => {
    const brand = summary?.payment_method_brand;
    const last4 = summary?.payment_method_last4;
    if (!brand || !last4) return L.paymentMethodNone;
    const niceBrand = brand.charAt(0).toUpperCase() + brand.slice(1);
    return `${niceBrand} **** ${last4}`;
  };

  return (
    <div className="space-y-4" data-testid="my-subscription-panel">
      <button
        onClick={onBack}
        className="text-sm text-indigo-400 hover:text-indigo-300 mb-1 flex items-center gap-1"
        data-testid="my-subscription-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> {L.back}
      </button>

      <div>
        <h3 className="text-xl font-bold text-white" data-testid="my-subscription-title">
          {L.title}
        </h3>
        <p className="text-sm text-zinc-400 mt-1">{L.subtitle}</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> <span>{L.loading}</span>
        </div>
      ) : (
        <>
          {/* Tarjeta principal con resumen del plan. */}
          <div
            className="rounded-2xl border border-zinc-700 bg-gradient-to-br from-zinc-800/80 to-zinc-900 p-4"
            data-testid="my-subscription-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isPremium
                  ? 'bg-gradient-to-br from-amber-500/30 to-fuchsia-500/20 border border-amber-500/40 text-amber-300'
                  : 'bg-zinc-700/60 border border-zinc-600 text-zinc-300'
              }`}>
                {isPremium ? <Crown className="w-5 h-5" /> : <SettingsIcon className="w-5 h-5" />}
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">{L.cardTitle}</p>
                <p className="text-base font-semibold text-white">
                  {isPremium ? L.planPremium : L.planFree}
                </p>
              </div>
            </div>

            <div className="mt-3">
              <Row label={L.plan} value={isPremium ? L.planPremium : L.planFree} testId="row-plan" />
              <Row label={L.status} value={renderStatus()} testId="row-status" />
              <Row label={L.renewal} value={summary?.renewal_date || '—'} testId="row-renewal" />
              <Row label={L.paymentMethod} value={renderPaymentMethod()} testId="row-payment-method" />
              <Row label={L.price} value={isPremium ? L.pricePremium : L.priceFree} testId="row-price" />
            </div>
          </div>

          {/* 🟥 Cuadro Negro Beneficios Premium oficiales (feb-2026) —
              fuente única `PREMIUM_BENEFITS`. §4.1: 100% ancho, padding
              16px, texto centrado en mobile, spacing vertical 10px. */}
          <div
            className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-4 w-full"
            data-testid="my-subscription-benefits"
          >
            <p className="text-xs uppercase tracking-wider text-amber-300/80 mb-2 text-center md:text-left">
              Tu Plan Premium incluye
            </p>
            <ul className="space-y-2.5">
              {PREMIUM_BENEFITS.map((text) => (
                <li
                  key={text}
                  className="flex items-start gap-2 text-sm text-zinc-200 justify-start"
                >
                  <Crown className="shrink-0 w-3.5 h-3.5 mt-1 text-amber-300" />
                  <span className="break-words">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Botones de acción — todos los exigidos por el Cuadro Negro.
              §4.2: "Actualizar a Premium" sticky al pie en mobile cuando
              el usuario es Free. */}
          <div className="space-y-2 pt-1">
            {!isPremium && (
              <button
                type="button"
                onClick={handleUpgrade}
                data-testid="btn-upgrade-premium"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-zinc-950 bg-gradient-to-r from-amber-400 to-fuchsia-400 hover:from-amber-300 hover:to-fuchsia-300 shadow-lg shadow-amber-500/20 transition-all sticky bottom-0 z-10"
              >
                <Crown className="w-4 h-4" /> {L.btnUpgrade}
              </button>
            )}

            <button
              type="button"
              onClick={() => handlePortal('payment_method_update', 'change-card')}
              disabled={actionLoading !== null}
              data-testid="btn-change-card"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading === 'change-card'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <CreditCard className="w-4 h-4" />}
              {L.btnChangeCard}
            </button>

            <button
              type="button"
              onClick={() => handlePortal('invoice_history', 'invoices')}
              disabled={actionLoading !== null}
              data-testid="btn-view-invoices"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-orange-600 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading === 'invoices'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Receipt className="w-4 h-4" />}
              {L.btnInvoices}
            </button>

            {isPremium && (
              <button
                type="button"
                onClick={() => handlePortal('subscription_cancel', 'cancel-sub')}
                disabled={actionLoading !== null}
                data-testid="btn-cancel-subscription"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading === 'cancel-sub'
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <XCircle className="w-4 h-4" />}
                {L.btnCancel}
              </button>
            )}

            <button
              type="button"
              onClick={() => handlePortal(null, 'full-portal')}
              disabled={actionLoading !== null}
              data-testid="btn-full-portal"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading === 'full-portal'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Sparkles className="w-4 h-4" />}
              {L.btnFullPortal}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default MySubscriptionPanel;
